import { useQuery } from '@tanstack/react-query';
import {
  Check,
  ChevronDown,
  CircleAlert,
  CircleCheck,
  Clock,
  Search,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import Transition from '@/components/Common/Transition';
import { ScheduleSelect } from '@/components/ScheduleSelect/ScheduleSelect';
import TreeSelect from '@/components/Select/TreeSelect';
import { Input } from '@/components/shadcn/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/shadcn/ui/popover';
import { ScrollArea } from '@/components/shadcn/ui/scroll-area';
import { Separator } from '@/components/shadcn/ui/separator';
import dashboardTreeFetcher from '@/features/Dashboard/utils/dashboardTreeFetcher';
import { cn } from '@/lib/cn';
import service from '@/services/index';
import { getTimezoneList } from '@/utils/util';

import { useSubscription2Context } from './SubscriptionContextProvider';

const timezoneList = getTimezoneList().map((zone) => ({
  label: zone.name,
  value: zone.tzCode,
}));

type Props = {
  dashboardAlias?: string;
};

const TriggerBox = (props: Props) => {
  const context = useSubscription2Context();
  const { state, setValue } = context.triggerState;
  const validated = context.triggerValidated;
  const [timezoneComboboxOpen, setTimezoneComboboxOpen] = useState(false);
  const [timezoneSearch, setTimezoneSearch] = useState('');
  const timezoneSearchInputRef = useRef<HTMLInputElement>(null);

  const selectedTimezone = useMemo(
    () => timezoneList.find((tz) => tz.value === state.timezone),
    [state.timezone],
  );

  const filteredTimezoneList = useMemo(() => {
    const search = timezoneSearch.trim().toLowerCase();
    if (!search) return timezoneList;
    return timezoneList.filter((tz) => {
      return (
        tz.label.toLowerCase().includes(search) ||
        tz.value.toLowerCase().includes(search)
      );
    });
  }, [timezoneSearch]);

  useEffect(() => {
    if (!timezoneComboboxOpen) {
      setTimezoneSearch('');
      return;
    }

    const timer = window.setTimeout(() => {
      timezoneSearchInputRef.current?.focus();
    }, 100);

    return () => window.clearTimeout(timer);
  }, [timezoneComboboxOpen]);

  const { data: dashboardList } = useQuery({
    queryKey: ['dashboard2-list-flat'],
    queryFn: async () => {
      const res = await service.dashboard2.fetchDashboardTreeList();
      const folderPromises =
        res.dashboard_folders?.map((f) =>
          service.dashboard2.fetchDashboardsInFolder(f.alias),
        ) || [];
      const folderDashboards = await Promise.all(folderPromises);
      return [
        ...folderDashboards.flatMap((f) => f.dashboards || []),
        ...(res.dashboards || []),
      ];
    },
  });

  useEffect(() => {
    if (props.dashboardAlias) {
      const target = dashboardList?.find(
        (d) => d.alias === props.dashboardAlias,
      );
      if (target) {
        setValue('target', target.alias);
        context.setHasChange(false);
      }
    }
  }, [dashboardList, props.dashboardAlias]);

  return (
    <div className="flex flex-col gap-4 rounded-md border p-6">
      <div className="flex items-center gap-2">
        {validated.validated ? (
          <CircleCheck className="size-5 text-green-500" />
        ) : (
          <CircleAlert className="size-5 text-amber-500" />
        )}
        <span className="text-sm font-semibold">Trigger</span>
        {!validated.validated && (
          <span className="text-sm text-muted-foreground">{validated.message}</span>
        )}
      </div>
      <Separator />
      <div className="flex items-center gap-5">
        <div className="w-[120px]">
          <span className="text-sm font-semibold text-muted-foreground">Report</span>
        </div>
        <div className="flex items-center gap-3">
          {props.dashboardAlias ? (
            <>
              {
                dashboardList?.find((d) => d.alias === props.dashboardAlias)
                  ?.title
              }
            </>
          ) : (
            <TreeSelect
              mode="single"
              style={{ width: 300 }}
              placeholder="Select dashboard"
              values={state.target ? [state.target] : []}
              fetcher={dashboardTreeFetcher}
              onChange={(values) => {
                setValue('target', values[0] as string);
              }}
            />
          )}
        </div>
      </div>
      <Transition status={!!state.target}>
        <div className="flex items-center gap-5">
          <div className="w-[120px]">
            <span className="text-sm font-semibold text-muted-foreground">Local time zone</span>
          </div>
          <div className="flex items-center gap-3">
            <Popover
              open={timezoneComboboxOpen}
              onOpenChange={setTimezoneComboboxOpen}
            >
              <PopoverTrigger asChild>
                <button
                  type="button"
                  role="combobox"
                  aria-expanded={timezoneComboboxOpen}
                  className="flex h-9 w-[300px] items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-left text-sm shadow-xs transition-colors hover:bg-muted/50 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none"
                >
                  <span
                    className={cn(
                      'truncate',
                      !selectedTimezone && 'text-muted-foreground',
                    )}
                  >
                    {selectedTimezone?.label || 'Select'}
                  </span>
                  <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="z-[9600] w-[300px] p-0"
                align="start"
              >
                <div className="border-b p-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      ref={timezoneSearchInputRef}
                      value={timezoneSearch}
                      onChange={(event) =>
                        setTimezoneSearch(event.target.value)
                      }
                      placeholder="Search time zones..."
                      className="h-8 pl-8"
                    />
                  </div>
                </div>
                <ScrollArea className="max-h-[260px]">
                  {filteredTimezoneList.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">
                      No time zones found
                    </div>
                  ) : (
                    <div className="p-1">
                      {filteredTimezoneList.map((tz) => {
                        const selected = tz.value === state.timezone;
                        return (
                          <button
                            key={tz.value}
                            type="button"
                            onClick={() => {
                              setValue('timezone', tz.value);
                              setTimezoneComboboxOpen(false);
                            }}
                            className={cn(
                              'flex w-full cursor-pointer items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground',
                              selected && 'bg-accent/50',
                            )}
                          >
                            <span className="truncate">{tz.label}</span>
                            {selected && (
                              <Check className="ml-2 size-4 shrink-0" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </Transition>
      <Transition status={!!state.target}>
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-5">
            <div className="flex w-[120px] items-center gap-1.5">
              <Clock className="size-4" />
              <span className="text-sm font-semibold text-muted-foreground">When</span>
            </div>
            <ScheduleSelect
              schedule={state.schedule}
              setSchedule={(schedule) => setValue('schedule', schedule)}
            />
          </div>
        </div>
      </Transition>
    </div>
  );
};

export default TriggerBox;
